/*
  page.subscribe.js — Coffee subscriptions.

  A scroll-driven three.js coffee rack (exported from Blender with ThreeJS Studio) with the
  add-to-cart panel beside it. Scrolling orbits the camera around the rack; whichever box is
  front-and-centre becomes the coffee loaded into the panel. Clicking a box, or the arrows,
  scrolls the page to that coffee.

  Everything the panel needs (variants, selling plans, prices) is in #sub3d-data, written by
  templates/page.subscribe.liquid. The three.js stack is imported lazily so the panel keeps
  working (with a static image) if WebGL or the CDN is unavailable.

  Dev helpers:  window.sub3d.select(i) | .goTo(i) | .calibrate()  (logs where each box takes over)
*/

const root = document.querySelector('[data-sub3d]');

// Before `load`, or a refresh puts the page back where the last visit had scrolled.
if (root && 'scrollRestoration' in history) history.scrollRestoration = 'manual';

// ------------------------------------------------------------------------------------------
// helpers
// ------------------------------------------------------------------------------------------
const $ = (sel, ctx = root) => ctx.querySelector(sel);
const clamp = (x, a, b) => Math.min(b, Math.max(a, x));
const pad2 = (n) => String(n).padStart(2, '0');
const isPhone = () => window.matchMedia('(max-width: 800px)').matches;
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Shopify money_format ("${{amount}}", "€{{amount_with_comma_separator}}" ...) → string. */
function formatMoney(cents, format) {
  const value = (Number(cents) || 0) / 100;
  const dec = (n, d, t) => {
    const [i, f] = n.toFixed(2).split('.');
    return i.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (d ? d + f : '');
  };
  const map = {
    amount: dec(value, '.', ','),
    amount_no_decimals: dec(value, '', ','),
    amount_with_comma_separator: dec(value, ',', '.'),
    amount_no_decimals_with_comma_separator: dec(value, '', '.'),
    amount_with_apostrophe_separator: dec(value, '.', "'"),
    amount_no_decimals_with_space_separator: dec(value, '', ' '),
    amount_with_space_separator: dec(value, ',', ' '),
    amount_with_period_and_space_separator: dec(value, '.', ' '),
  };
  const tpl = (format || '${{amount}}').replace(/<[^>]*>/g, '');
  return tpl.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => map[key] ?? map.amount).trim();
}

/** "10oz" → "10 oz", "2lbs" → "2 lbs", "2.2kg" → "2.2 kg". Anything else is returned as-is. */
function prettySize(title) {
  const m = /^(\d+(?:\.\d+)?)\s*([a-z]+)$/i.exec(String(title).trim());
  return m ? `${m[1]} ${m[2].toLowerCase()}` : title;
}

/** Variants we never sell on subscription here (samples and wholesale case packs). */
const SIZE_SKIP = /case pack|^\s*(2\s*oz|4\s*oz|113\s*g|17\.6\s*kg|6 x 284\s*g)\s*$/i;

/** "2 lbs" / "2lb" / "2LBS" all mean the same size: compare sizes across coffees by this key. */
const sizeKey = (title) => String(title || '').toLowerCase().replace(/\s+/g, '').replace(/lbs$/, 'lb');

/** 10 oz is a week for one person; the bigger bags scale from that. */
const SIZE_NOTES = {
  '10oz': 'About 15 cups. A week of coffee for one person.',
  '284g': 'About 15 cups. A week of coffee for one person.',
  '2lb': 'About 45 cups. About three weeks for one person, or a week for a small household.',
  '907g': 'About 45 cups. About three weeks for one person, or a week for a small household.',
  '5lb': 'About 110 cups. About two months for one person, or a couple of weeks for the office.',
  '2.27kg': 'About 110 cups. About two months for one person, or a couple of weeks for the office.',
  '2268g': 'About 110 cups. About two months for one person, or a couple of weeks for the office.',
};
const sizeNote = (size) => (size && SIZE_NOTES[size.key]) || 'Whole bean coffee, ground when you are ready to brew.';

/** GLB object name → the box it belongs to: "SouthernWeather002" → "SouthernWeather", "_Blends__ml3" → "_Blends". */
const baseName = (name) => String(name || '').replace(/__ml\d+$/, '').replace(/\d{3}$/, '');

function cadenceOf(plan) {
  const m = /every\s+(\d+)\s*(day|week|month|year)/i.exec(plan.name || '') || [];
  const n = parseInt(m[1] || '1', 10) || 1;
  const unit = (m[2] || 'week').toLowerCase();
  return { n, unit, key: `${n}-${unit}`, label: n === 1 ? `Every ${cap(unit)}` : `Every ${n} ${cap(unit)}s`,
           delivery: n === 1 ? `Delivered every ${unit}` : `Delivered every ${n} ${unit}s` };
}
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// ------------------------------------------------------------------------------------------
// boot
// ------------------------------------------------------------------------------------------
async function boot(root) {
  const data = JSON.parse($('#sub3d-data', document).textContent);
  const cfg = data.config || {};
  applyAssetOverride(cfg);
  const products = (data.products || []).filter((p) => p && p.id).map((p, i) => ({
    ...p,
    index: i,
    from: Number(p.from) || 0,
    at: clamp(Number(p.at) || 0, 0, 1),
    sizes: sizeOptions(p),
    cadences: cadenceOptions(p),
  }));
  if (!products.length) throw new Error('no products in the line-up resolved — check the handles in page.subscribe.liquid');

  const panel = createPanel(root, products, cfg);
  const initial = initialIndex(products);
  panel.select(initial, { animate: false, url: false });

  const scene = await createScene(root, products, cfg, panel, initial).catch((err) => {
    console.warn('[subscribe] 3D unavailable, using the static fallback:', err);
    return null;
  });

  if (!scene) {
    root.dataset.state = 'fallback';
    panel.setNavigator({ goTo: (i) => panel.select(i) });
    return;
  }
  panel.setNavigator(scene);
  // Land on the deep-linked coffee, unless the shopper has already started scrolling while we loaded.
  // The browser runs its own scroll restoration at `load`, which would undo an earlier jump — so wait for it
  // and keep the position ours from then on (the URL's product-id is the state we restore from anyway).
  await new Promise((r) => (document.readyState === 'complete' ? r() : window.addEventListener('load', () => r(), { once: true })));
  // The first coffee is centred a little way down the orbit (its "at"). Landing there on load
  // leaves the page partly scrolled. Its opening frame is the top. Later coffees still jump
  // to their centre, and a shopper who has already scrolled a viewport keeps that place.
  if (initial === 0 && window.scrollY < window.innerHeight) scene.toTop();
  else if (window.scrollY < 10) scene.goTo(initial, { immediate: true });
  else scene.sync();
}

/**
 * The GLB is a theme asset (asset_url). The HDR lives in Shopify's Content > Files.
 * While developing, `?sub3d-assets=http://127.0.0.1:8765/ref/` (or localStorage 'sub3d-assets') loads
 * both from another host instead — that host has to allow CORS. The query parameter sticks for the session.
 */
function applyAssetOverride(cfg) {
  let base = null;
  try {
    const q = new URLSearchParams(location.search).get('sub3d-assets');
    if (q === '') sessionStorage.removeItem('sub3d-assets');
    else if (q) sessionStorage.setItem('sub3d-assets', q);
    base = sessionStorage.getItem('sub3d-assets') || localStorage.getItem('sub3d-assets');
  } catch { /* storage unavailable */ }
  if (!base) return;
  if (!base.endsWith('/')) base += '/';
  const fileName = (url) => decodeURIComponent(String(url || '').split('?')[0].split('/').pop());
  if (cfg.glb) cfg.glb = base + fileName(cfg.glb);
  if (cfg.hdr) cfg.hdr = base + fileName(cfg.hdr);
  console.info('[subscribe] loading the 3D files from', base);
}

function initialIndex(products) {
  const q = new URLSearchParams(location.search);
  const id = q.get('product-id') || q.get('product_id');
  const handle = q.get('product');
  let i = products.findIndex((p) => (id && String(p.id) === String(id)) || (handle && p.handle === handle));
  if (i < 0) i = 0;
  return i;
}

function sizeOptions(product) {
  const variants = (product.variants || []).filter((v) => !SIZE_SKIP.test(v.title || ''));
  return variants.map((v) => ({ id: v.id, title: v.title, key: sizeKey(v.title), label: prettySize(v.title), available: v.available !== false, variant: v }));
}

function cadenceOptions(product) {
  const groups = product.selling_plan_groups || [];
  const group = groups.find((g) => String(g.app_id) === '294517' && g.selling_plans && g.selling_plans.length)
             || groups.find((g) => g.selling_plans && g.selling_plans.length);
  if (!group) return [];
  return group.selling_plans.map((plan) => ({ id: plan.id, plan, ...cadenceOf(plan) }));
}

// ------------------------------------------------------------------------------------------
// the add-to-cart panel
// ------------------------------------------------------------------------------------------
function createPanel(root, products, cfg) {
  const el = {
    title: $('.sub3d__title'),
    notes: $('.sub3d__notes'),
    scales: $('.sub3d__scales'),
    flavor: $('.sub3d__flavor'),
    dots: $('.sub3d__dots'),
    roast: $('.sub3d__roast'),
    roastFill: $('.sub3d__roast-bar i'),
    roastLabel: $('.sub3d__roast-label'),
    titleWrap: $('.sub3d__title-wrap'),
    amount: $('#sub3d-amount'),
    cadence: $('#sub3d-cadence'),
    tip: $('[data-tip]'),
    now: $('.sub3d__price-now'),
    was: $('.sub3d__price-was'),
    delivery: $('.sub3d__delivery'),
    atc: $('.sub3d__atc'),
    form: $('.sub3d__form'),
    prev: $('.sub3d__arrow--prev'),
    next: $('.sub3d__arrow--next'),
    countNow: $('.sub3d__count-now'),
    countTotal: $('.sub3d__count-total'),
    detail: $('.sub3d__detail'),
    fallback: $('.sub3d__fallback'),
  };

  const state = { index: -1, sizeKey: null, cadenceKey: null, busy: false };
  let navigator = { goTo: (i) => select(i) };
  let switchTimer = 0;

  el.countTotal.textContent = pad2(products.length);

  function fillNotes(product) {
    const notes = Array.isArray(product.notes) ? product.notes.map((n) => String(n).trim()).filter(Boolean) : [];
    el.notes.replaceChildren();
    if (notes.length) {
      for (const note of notes) {
        const span = document.createElement('span');
        span.className = 'note';
        span.textContent = note;
        el.notes.appendChild(span);
      }
      el.notes.hidden = false;
      return;
    }
    const subtitle = String(product.subtitle || '').trim();
    el.notes.textContent = subtitle;
    el.notes.hidden = !subtitle;
  }

  /** Agtron 70 sits at the dark end of the bar, 155 at the light end. Same span as the product page. */
  function roastBarWidth(score) {
    const n = Number(score);
    if (!Number.isFinite(n)) return 0;
    return clamp((n - 70) / 85 * 100, 0, 100);
  }

  function fillScales(product) {
    const trad = Number(product.tradMod);
    const hasTrad = Number.isFinite(trad) && trad > 0;
    el.flavor.hidden = !hasTrad;
    if (hasTrad) {
      const score = clamp(Math.round(trad), 1, 8);
      el.dots.setAttribute('aria-label', `${score} of 8 from traditional to modern`);
      el.dots.replaceChildren();
      for (let i = 1; i <= 8; i++) {
        const dot = document.createElement('i');
        if (i <= score) dot.className = 'is-on';
        el.dots.appendChild(dot);
      }
    }

    const roast = Number(product.roast);
    const hasRoast = Number.isFinite(roast) && roast > 0;
    el.roast.hidden = !hasRoast;
    if (hasRoast) {
      el.roastFill.style.width = `${roastBarWidth(roast)}%`;
      const label = String(product.roastLabel || '').trim();
      const named = label && label.toLowerCase() !== 'unknown';
      el.roastLabel.textContent = named ? `${label} · Agtron ${roast}` : `Agtron ${roast}`;
    }

    el.scales.hidden = !hasTrad && !hasRoast;
  }

  function current() {
    const product = products[state.index];
    if (!product) return {};
    const size = product.sizes.find((s) => s.key === state.sizeKey) || product.sizes[0];
    const cadence = product.cadences.find((c) => c.key === state.cadenceKey) || product.cadences[0];
    return { product, size, variant: size && size.variant, cadence, plan: cadence && cadence.plan };
  }

  function fillSelect(select, options, selectedValue) {
    select.innerHTML = '';
    for (const o of options) {
      const opt = document.createElement('option');
      opt.value = o.value;
      opt.textContent = o.label;
      if (o.disabled) opt.disabled = true;
      select.appendChild(opt);
    }
    if (options.some((o) => String(o.value) === String(selectedValue))) select.value = String(selectedValue);
    select.disabled = options.length === 0;
  }

  function render() {
    const { product } = current();
    if (!product) return;

    // sizes (keep the shopper's size when the next coffee has it; default 10oz / 284g)
    const preferred = product.sizes.find((s) => s.key === state.sizeKey)
                   || product.sizes.find((s) => /^(10oz|284g)$/.test(s.key) && s.available)
                   || product.sizes.find((s) => s.available) || product.sizes[0];
    state.sizeKey = preferred ? preferred.key : null;
    fillSelect(el.amount, product.sizes.map((s) => ({ value: s.key, label: s.available ? s.label : `${s.label} — sold out`, disabled: !s.available })), state.sizeKey);

    // cadence (keep the shopper's cadence when the next coffee offers it)
    const cad = product.cadences.find((c) => c.key === state.cadenceKey) || product.cadences[0];
    state.cadenceKey = cad ? cad.key : null;
    fillSelect(el.cadence, product.cadences.map((c) => ({ value: c.key, label: c.label })), state.cadenceKey);
    el.cadence.closest('.sub3d__field').hidden = product.cadences.length === 0;

    renderPrice();
  }

  function renderPrice() {
    const { product, size, variant, cadence, plan } = current();
    if (!product) return;
    const sellable = !!(variant && size && size.available);

    let now = variant ? variant.price : null;
    let was = variant ? variant.compare_at_price : null;
    if (variant && plan) {
      const alloc = (variant.selling_plan_allocations || []).find((a) => String(a.selling_plan_id) === String(plan.id));
      if (alloc) { now = alloc.per_delivery_price ?? alloc.price; was = alloc.compare_at_price; }
    }
    el.now.textContent = now != null ? formatMoney(now, cfg.moneyFormat) : '—';
    const showWas = was != null && now != null && was > now;
    el.was.hidden = !showWas;
    el.was.textContent = showWas ? formatMoney(was, cfg.moneyFormat) : '';
    el.delivery.textContent = cadence ? cadence.delivery : (plan ? '' : 'One-time purchase');

    el.tip.textContent = sizeNote(size);

    el.atc.disabled = !sellable || state.busy;
    if (!state.busy) el.atc.textContent = sellable ? el.atc.dataset.label : 'Sold out';
  }

  function select(index, opts = {}) {
    index = clamp(index, 0, products.length - 1);
    const changed = index !== state.index;
    state.index = index;
    const product = products[index];

    const apply = () => {
      el.title.textContent = product.title;
      fillNotes(product);
      fillScales(product);
      el.detail.href = product.url || '#';
      el.countNow.textContent = pad2(index + 1);
      el.prev.disabled = index === 0;
      el.next.disabled = index === products.length - 1;
      if (el.fallback && product.image) el.fallback.style.backgroundImage = `url("${product.image}")`;
      render();
    };

    clearTimeout(switchTimer);
    if (changed && opts.animate !== false && !reducedMotion()) {
      el.titleWrap.classList.add('is-switching');
      switchTimer = setTimeout(() => { apply(); el.titleWrap.classList.remove('is-switching'); }, 180);
    } else {
      apply();
      el.titleWrap.classList.remove('is-switching');
    }

    if (changed && opts.url !== false) {
      const url = new URL(location.href);
      url.searchParams.set('product-id', String(product.id));
      url.searchParams.delete('product');
      history.replaceState(history.state, '', url);
    }
    return product;
  }

  // ---- events
  el.amount.addEventListener('change', () => { state.sizeKey = el.amount.value; renderPrice(); });
  el.cadence.addEventListener('change', () => { state.cadenceKey = el.cadence.value; renderPrice(); });
  el.prev.addEventListener('click', () => navigator.goTo(state.index - 1));
  el.next.addEventListener('click', () => navigator.goTo(state.index + 1));
  el.form.addEventListener('submit', (e) => { e.preventDefault(); addToCart(); });
  document.addEventListener('keydown', (e) => {
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
    if (/^(INPUT|SELECT|TEXTAREA|BUTTON)$/.test((e.target && e.target.tagName) || '')) return;
    if (e.key === 'ArrowRight') { e.preventDefault(); navigator.goTo(state.index + 1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); navigator.goTo(state.index - 1); }
  });

  async function addToCart() {
    const { variant, size, cadence, plan } = current();
    if (!variant || !size || !size.available || state.busy) return;
    state.busy = true;
    el.atc.disabled = true;
    el.atc.textContent = 'Adding…';

    const properties = {};
    if (plan) {
      properties.selling_plan = plan.id;                      // CartJS sends this one at the top level
      properties.shipping_interval_frequency = cadence.n;     // kept for parity with the rest of the theme
    }

    const done = (line) => {
      state.busy = false;
      el.atc.textContent = 'Added';
      setTimeout(() => renderPrice(), 1600);
      if (typeof window.show_cart_confirm === 'function' && line) window.show_cart_confirm(line);
      else location.href = cfg.cartUrl || '/cart';
    };
    const fail = (err) => {
      console.error('[subscribe] add to cart failed', err);
      state.busy = false;
      el.atc.textContent = 'Try again';
      el.atc.disabled = false;
      setTimeout(() => renderPrice(), 2500);
    };

    if (window.CartJS && typeof window.CartJS.addItem === 'function') {
      window.CartJS.addItem(variant.id, 1, properties, { success: done, error: fail });
      return;
    }
    try {
      const body = { id: variant.id, quantity: 1 };
      if (plan) { body.selling_plan = plan.id; body.properties = { shipping_interval_frequency: cadence.n }; }
      const res = await fetch((cfg.cartUrl || '/cart').replace(/\/cart$/, '') + '/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      done(await res.json());
    } catch (err) { fail(err); }
  }

  return {
    select,
    get index() { return state.index; },
    setNavigator(n) { navigator = n; },
  };
}

// ------------------------------------------------------------------------------------------
// the 3D rack
// ------------------------------------------------------------------------------------------
async function createScene(root, products, cfg, panel, initial = 0) {
  const stage = $('.sub3d__stage');
  const track = $('.sub3d__track');
  const canvas = $('.sub3d__canvas');
  const loaderBar = $('.sub3d__loader-bar i');
  const hint = $('.sub3d__hint');

  if (!canvas || !cfg.glb) throw new Error('no canvas or model url');
  if (!hasWebGL()) throw new Error('WebGL is not available');

  // the heavy stack, loaded only now so a CDN hiccup still leaves a working page
  const [THREE, { GLTFLoader }, { DRACOLoader }, { KTX2Loader }, { HDRLoader }, { default: Lenis }, { createDirector }, { createPostFX }] =
    await Promise.all([
      import('three'),
      import('three/addons/loaders/GLTFLoader.js'),
      import('three/addons/loaders/DRACOLoader.js'),
      import('three/addons/loaders/KTX2Loader.js'),
      import('three/addons/loaders/HDRLoader.js'),
      import('lenis'),
      import('scroll-director'),
      import('scroll-postfx'),
    ]);

  // ---- renderer
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, premultipliedAlpha: true, powerPreference: 'high-performance' });
  renderer.toneMapping = THREE.NoToneMapping;             // the GLB's lights are calibrated for this (see README)
  let pixelRatio = Math.min(window.devicePixelRatio || 1, isPhone() ? 2 : 1.5);
  renderer.setPixelRatio(pixelRatio);

  // ---- load the model (Draco meshes + KTX2 textures) and the HDR together
  const draco = new DRACOLoader().setDecoderPath(cfg.decoders.draco);
  const ktx2 = new KTX2Loader().setTranscoderPath(cfg.decoders.basis).detectSupport(renderer);
  const gltfLoader = new GLTFLoader().setDRACOLoader(draco).setKTX2Loader(ktx2);
  const progress = (e) => { if (loaderBar && e.total) loaderBar.style.transform = `scaleX(${clamp(e.loaded / e.total, 0, 1) * 0.9})`; };
  const [gltf, hdr] = await Promise.all([
    gltfLoader.loadAsync(cfg.glb, progress).catch((err) => {
      throw new Error(`could not load the 3D model at ${cfg.glb} — check assets/onyx-subscribe-asset.glb is in the theme, `
        + `or point ?sub3d-assets= at a CORS-enabled host while developing (${err && err.message ? err.message : err})`);
    }),
    cfg.hdr ? new HDRLoader().loadAsync(cfg.hdr).catch((err) => { console.warn('[subscribe] HDR failed, lights only', err); return null; }) : null,
  ]);
  if (loaderBar) loaderBar.style.transform = 'scaleX(0.95)';

  const scene = new THREE.Scene();
  scene.add(gltf.scene);

  // ---- sizing state (declared before the director: its camera getter can call frameCamera during setup)
  let size = { w: 1, h: 1 };
  // The stage's CSS gradient is the background. Hide any backdrop mesh the export still carries.
  const backdrop = gltf.scene.getObjectByName('Pole-Background');
  if (backdrop) backdrop.visible = false;

  // ---- director: scroll → timeline, hover reactions, environment, shadows
  const director = createDirector(gltf, {
    onCamera: (cam) => frameCamera(cam),
  });
  director.applyBackground(scene, renderer);
  scene.background = null;                                 // page shows through; postfx reads this and keeps the alpha
  renderer.setClearColor(0x000000, 0);
  if (hdr) director.applyEnvironment(scene, hdr, { background: false });
  director.enableShadows(renderer, { mapSize: isPhone() ? 1024 : 2048 });
  const fx = createPostFX(renderer, scene, director);
  const exposure = Number(cfg.fxExposure) || 0;
  if (exposure && fx.active && fx.settings && fx.settings.grade) fx.set('grade', { exposure: fx.settings.grade.exposure + exposure });
  director.enableInteraction(renderer.domElement);        // the boxes lift when hovered (set up in Blender)
  const camera = () => director.camera;

  // ---- which GLB objects are coffees we sell
  const byMesh = new Map(products.map((p) => [p.mesh, p]));
  const boxMeshes = [];
  gltf.scene.traverse((o) => {
    if (!o.isMesh) return;
    for (let n = o; n && n !== gltf.scene; n = n.parent) {
      if (byMesh.has(baseName(n.name))) { boxMeshes.push(o); o.userData.sub3dProduct = byMesh.get(baseName(n.name)); break; }
    }
  });

  // ---- framing: full-bleed canvas; on wide screens the rack is pushed left to make room for the panel
  function frameCamera(cam) {
    if (!cam || !cam.isPerspectiveCamera) return;
    const { w, h } = size;
    const phone = isPhone();
    if (cam.userData.studioFov === undefined) cam.userData.studioFov = cam.fov;
    // portrait screens: open the field of view a little so the rack is not cropped to a sliver
    const own = cam.userData.studioFov;
    const aspect = w / h;
    const widen = aspect < 1
      ? Math.min(own * 1.45, THREE.MathUtils.radToDeg(2 * Math.atan(Math.tan(THREE.MathUtils.degToRad(own) / 2) / Math.max(aspect, 0.45))))
      : own;
    cam.fov = widen;
    cam.aspect = aspect;
    cam.zoom = phone ? 1.6 : 1;                            // phones: tighter crop, same centre
    // Wide screens: rack centre lands at ~30% of the width, as in the comp.
    // Phones: the panel is a bottom sheet, so lift the rack a little above centre.
    const shiftX = phone ? 0 : Math.round(w * 0.2);
    const shiftY = phone ? Math.round(h * 0.08) : 0;
    if (shiftX || shiftY) cam.setViewOffset(w, h, shiftX, shiftY, w, h);
    else cam.clearViewOffset();
    cam.updateProjectionMatrix();
  }
  function resize() {
    const w = Math.max(1, stage.clientWidth), h = Math.max(1, stage.clientHeight);
    size = { w, h };
    renderer.setSize(w, h, false);
    director.resize(w, h);
    frameCamera(camera());
    layoutTrack();
  }

  // ---- the page length: the stage sticks for pageVh viewport heights, then the footer follows
  const pageVh = Number(cfg.scrollVh) > 0 ? Number(cfg.scrollVh) : director.pageVh;
  function layoutTrack() { track.style.height = `calc(${pageVh}vh + 100svh)`; }
  const trackTop = () => track.getBoundingClientRect().top + window.scrollY;
  const scrollLength = () => Math.max(1, track.offsetHeight - stage.offsetHeight);
  const scrollYFor = (p) => trackTop() + clamp(p, 0, 1) * scrollLength();

  // ---- smooth scroll (Lenis keeps its own rAF so the page scrolls even when we pause rendering)
  const lenis = new Lenis({
    autoRaf: true,
    lerp: 0.1,
    smoothWheel: !reducedMotion(),
    wheelMultiplier: 0.9,
    allowNestedScroll: true,
  });

  // ---- scroll ↔ product
  let pinned = initial;                 // the coffee we are travelling to (arrows / click / deep link): the panel shows it right away
  let hintDone = false;
  function progressNow() {
    const y = window.scrollY - trackTop();
    return clamp(y / scrollLength(), 0, 1);
  }
  function indexFor(p) {
    let i = 0;
    for (let k = 1; k < products.length; k++) if (p >= products[k].from - 1e-6) i = k;
    return i;
  }
  function syncScroll() {
    const p = progressNow();
    director.setScrollVh(p * director.pageVh);
    if (!hintDone && p > 0.02) { hintDone = true; hint && hint.classList.add('is-done'); }
    const i = pinned >= 0 ? pinned : indexFor(p);
    if (i !== panel.index) panel.select(i);
  }
  lenis.on('scroll', syncScroll);
  window.addEventListener('scroll', syncScroll, { passive: true });

  let trip = 0;
  function finishTrip(id, i) {
    if (id !== trip) return;                                 // a newer click owns the scroll now
    if (pinned === i) { pinned = -1; syncScroll(); }
    if (lenis.isLocked) lenis.isLocked = false;
  }
  function goTo(i, opts = {}) {
    i = clamp(i, 0, products.length - 1);
    const y = scrollYFor(products[i].at);
    const id = ++trip;
    const from = panel.index;
    pinned = i;
    panel.select(i, { animate: !opts.immediate });
    if (opts.immediate) {
      lenis.scrollTo(y, { immediate: true, force: true });
      window.scrollTo(0, y);
      pinned = -1;
      director.setScrollVh(progressNow() * director.pageVh);
      director.snap();
      return;
    }
    const duration = clamp(0.7 + Math.abs(i - from) * 0.25, 0.9, 1.8);
    lenis.scrollTo(y, {
      duration,
      easing: (t) => 1 - Math.pow(1 - t, 3),
      lock: true,                                            // the trip always arrives; the panel can't drift meanwhile
      force: true,                                           // a click during a trip replaces it; lock would swallow it
      onComplete: () => finishTrip(id, i),
    });
    // belt and braces: never leave the page pinned or locked if the animation never reports back
    setTimeout(() => finishTrip(id, i), duration * 1000 + 600);
  }

  function toTop() {
    const y = scrollYFor(0);
    pinned = -1;
    lenis.scrollTo(y, { immediate: true, force: true });
    window.scrollTo(0, y);
    director.setScrollVh(0);
    director.snap();
    syncScroll();
  }

  // ---- click a box to pick that coffee (hover gives a pointer cursor; a drag is not a click)
  const ray = new THREE.Raycaster();
  const ndc = new THREE.Vector2();
  function pick(x, y) {
    const r = canvas.getBoundingClientRect();
    ndc.set(((x - r.left) / r.width) * 2 - 1, -((y - r.top) / r.height) * 2 + 1);
    const cam = camera();
    if (!cam) return null;
    ray.setFromCamera(ndc, cam);
    const hits = ray.intersectObjects(boxMeshes, false);
    for (const h of hits) {
      let visible = true;
      for (let o = h.object; o; o = o.parent) if (!o.visible) { visible = false; break; }
      if (visible) return h.object.userData.sub3dProduct || null;
    }
    return null;
  }
  let down = null, lastHover = 0;
  canvas.addEventListener('pointerdown', (e) => { down = { x: e.clientX, y: e.clientY, t: performance.now() }; });
  canvas.addEventListener('pointerup', (e) => {
    if (!down) return;
    const moved = Math.hypot(e.clientX - down.x, e.clientY - down.y);
    const quick = performance.now() - down.t < 600;
    down = null;
    if (moved > 8 || !quick) return;
    const product = pick(e.clientX, e.clientY);
    if (product) goTo(product.index);
  });
  canvas.addEventListener('pointermove', (e) => {
    if (e.pointerType === 'touch') return;
    const now = performance.now();
    if (now - lastHover < 70) return;
    lastHover = now;
    stage.style.cursor = pick(e.clientX, e.clientY) ? 'pointer' : '';
  });
  canvas.addEventListener('pointerleave', () => { stage.style.cursor = ''; });

  // ---- render loop (paused while the stage is off screen or the tab is hidden)
  const timer = new THREE.Timer();
  let raf = 0, visible = true, hidden = document.hidden, frames = 0, slow = 0, degrade = 0;
  function frame(ms) {
    raf = requestAnimationFrame(frame);
    timer.update(ms);
    const dt = Math.min(timer.getDelta(), 0.1);
    director.update(dt);
    fx.render();
    adapt(dt);
    if (frames++ === 0) ready();
  }
  function start() { if (!raf && visible && !hidden) { timer.reset?.(); raf = requestAnimationFrame(frame); } }
  function stop() { if (raf) { cancelAnimationFrame(raf); raf = 0; } }
  // if the device cannot hold ~40 fps, trade pixels first and the depth of field second
  function adapt(dt) {
    if (frames < 30) return;
    slow = dt > 1 / 38 ? slow + 1 : Math.max(0, slow - 1);
    if (slow < 45 || degrade > 1) return;
    slow = 0; degrade++;
    if (degrade === 1) { pixelRatio = 1; renderer.setPixelRatio(pixelRatio); resize(); }
    else { fx.setQuality('light'); }
  }
  function ready() {
    root.dataset.state = 'ready';
    if (loaderBar) loaderBar.style.transform = 'scaleX(1)';
  }

  new ResizeObserver(() => resize()).observe(stage);
  new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; visible ? start() : stop(); }, { threshold: 0 }).observe(stage);
  document.addEventListener('visibilitychange', () => { hidden = document.hidden; hidden ? stop() : start(); });
  setTimeout(() => { if (!hintDone) { hintDone = true; hint && hint.classList.add('is-done'); } }, 7000);

  resize();
  director.snap();
  director.warmFades(renderer, scene);
  syncScroll();
  start();

  // ---- dev helpers
  window.sub3d = {
    director, lenis, renderer, scene, fx, products, goTo, pick, select: (i) => panel.select(i),
    /** Logs, for a sweep of the scroll, which coffee box is nearest the camera — use it to refresh "from"/"at" in the Liquid. */
    calibrate(samples = 200) {
      const cam = camera(), v = new THREE.Vector3(), c = new THREE.Vector3(), rows = [];
      const roots = [...new Set(boxMeshes.map((m) => { let n = m; while (n && !byMesh.has(baseName(n.name))) n = n.parent; return n; }))];
      const was = director.scrollVh;
      let last = null;
      for (let s = 0; s <= samples; s++) {
        const p = s / samples;
        director.setScrollVh(p * director.pageVh); director.snap();
        cam.updateMatrixWorld(true); c.setFromMatrixPosition(cam.matrixWorld);
        let best = null;
        for (const n of roots) {
          n.updateWorldMatrix(true, false); v.setFromMatrixPosition(n.matrixWorld);
          const d = v.distanceTo(c);
          if (!best || d < best.d) best = { d, name: baseName(n.name) };
        }
        if (best && best.name !== last) { rows.push({ from: +p.toFixed(3), box: best.name, handle: (byMesh.get(best.name) || {}).handle }); last = best.name; }
      }
      director.setScrollVh(was); director.snap();
      console.table(rows);
      return rows;
    },
  };

  return { goTo, toTop, sync: () => { pinned = -1; syncScroll(); }, director, lenis };
}

function hasWebGL() {
  try {
    const c = document.createElement('canvas');
    return !!(window.WebGL2RenderingContext && c.getContext('webgl2'));
  } catch { return false; }
}

if (root) boot(root).catch((err) => {
  console.error('[subscribe] failed to start', err);
  root.dataset.state = 'fallback';
});
